import React, { useState } from 'react';
import { useBible } from '../../contexts/BibleContext';
import Icon from '../UI/Icon';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './TranslationSelector.css';

// Recommended translations from our local collection
const RECOMMENDED_TRANSLATIONS = ['eng_niv', 'eng_esv', 'eng_kjv', 'eng_web', 'eng_oeb', 'gre_sblgn', 'heb_wlc', 'ara_svd'];

function TranslationSelector({ onClose }) {
  const { 
    availableBibles, 
    selectedTranslations, 
    addTranslation, 
    removeTranslation,
    setTranslations 
  } = useBible();
  
  const [searchQuery, setSearchQuery] = useState('');
  
  // Enable swipe-to-close on mobile
  const { ref: swipeRef } = useSwipeToClose(onClose);
  
  // Filter translations based on search
  const filteredTranslations = searchQuery
    ? availableBibles.filter(bible => 
        bible.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bible.abbreviation?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableBibles;
  
  // Prioritized/recommended translations
  const recommended = filteredTranslations.filter(b => RECOMMENDED_TRANSLATIONS.includes(b.id));
  const others = filteredTranslations.filter(b => !RECOMMENDED_TRANSLATIONS.includes(b.id));
  
  const isSelected = (bibleId) => selectedTranslations.includes(bibleId);
  
  const toggleTranslation = (bibleId) => {
    if (isSelected(bibleId)) {
      removeTranslation(bibleId);
    } else {
      addTranslation(bibleId);
    }
  };
  
  const moveUp = (bibleId) => {
    const index = selectedTranslations.indexOf(bibleId);
    if (index > 0) {
      const newOrder = [...selectedTranslations];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setTranslations(newOrder);
    }
  };
  
  const moveDown = (bibleId) => {
    const index = selectedTranslations.indexOf(bibleId);
    if (index < selectedTranslations.length - 1) {
      const newOrder = [...selectedTranslations];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setTranslations(newOrder);
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="translation-selector modal" ref={swipeRef} onClick={e => e.stopPropagation()}>
        <div className="modal-drag-handle" />
        <div className="modal-header">
          <h2 className="modal-title">Translations</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>
        
        {/* Selected Translations */}
        {selectedTranslations.length > 0 && (
          <div className="selected-section">
            <h3 className="section-label">
              Selected ({selectedTranslations.length}/4)
            </h3>
            <div className="selected-list">
              {selectedTranslations.map((bibleId, index) => {
                const bible = availableBibles.find(b => b.id === bibleId);
                return (
                  <div key={bibleId} className="selected-item">
                    <div className="selected-info">
                      <span className="selected-abbr">
                        {bible?.abbreviation || bible?.name?.substring(0, 3) || bibleId}
                      </span>
                      <span className="selected-name">{bible?.name}</span>
                    </div>
                    <div className="selected-actions">
                      <button 
                        className="btn btn-ghost btn-icon btn-sm"
                        onClick={() => moveUp(bibleId)}
                        disabled={index === 0}
                      >
                        <Icon name="chevronUp" size={16} />
                      </button>
                      <button 
                        className="btn btn-ghost btn-icon btn-sm"
                        onClick={() => moveDown(bibleId)}
                        disabled={index === selectedTranslations.length - 1}
                      >
                        <Icon name="chevronDown" size={16} />
                      </button>
                      <button 
                        className="btn btn-ghost btn-icon btn-sm"
                        onClick={() => removeTranslation(bibleId)}
                        disabled={selectedTranslations.length === 1}
                      >
                        <Icon name="x" size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Search */}
        <div className="search-container">
          <Icon name="search" size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search translations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        {/* Translation List */}
        <div className="translations-container">
          {recommended.length > 0 && (
            <div className="translation-group">
              <h3 className="group-label">Recommended</h3>
              {recommended.map(bible => (
                <button
                  key={bible.id}
                  className={`translation-item ${isSelected(bible.id) ? 'selected' : ''}`}
                  onClick={() => toggleTranslation(bible.id)}
                  disabled={isSelected(bible.id) ? selectedTranslations.length === 1 : selectedTranslations.length >= 4}
                >
                  <div className="translation-info">
                    <span className="translation-abbr">
                      {bible.abbreviation || bible.name?.substring(0, 3)}
                    </span>
                    <div className="translation-details">
                      <span className="translation-name">{bible.name}</span>
                      {bible.description && (
                        <span className="translation-desc">{bible.description}</span>
                      )}
                    </div>
                  </div>
                  <div className="translation-check">
                    {isSelected(bible.id) && <Icon name="check" size={18} />}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {others.length > 0 && (
            <div className="translation-group">
              <h3 className="group-label">All Translations</h3>
              {others.map(bible => (
                <button
                  key={bible.id}
                  className={`translation-item ${isSelected(bible.id) ? 'selected' : ''}`}
                  onClick={() => toggleTranslation(bible.id)}
                  disabled={isSelected(bible.id) ? selectedTranslations.length === 1 : selectedTranslations.length >= 4}
                >
                  <div className="translation-info">
                    <span className="translation-abbr">
                      {bible.abbreviation || bible.name?.substring(0, 3)}
                    </span>
                    <div className="translation-details">
                      <span className="translation-name">{bible.name}</span>
                      <span className="translation-lang">{bible.language?.name || bible.language}</span>
                    </div>
                  </div>
                  <div className="translation-check">
                    {isSelected(bible.id) && <Icon name="check" size={18} />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <p className="help-text">
            Select up to 4 translations to compare side by side
          </p>
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default TranslationSelector;
